import {
  SFX,
  SFX_POOL_DEFAULT,
  START_SFX_EARLY_WINDOW_MS,
  START_SFX_LEAD_MS,
  START_SFX_TIMER_LOOKAHEAD_MS,
  WEB_AUDIO_START_ADVANCE_SEC
} from '../engine/config.js';
import { settings } from '../ui/settings.js';
import { ui } from '../engine/state.js';
import { clamp } from '../utils/utils.js';
import { estimatedOneWayMs } from '../engine/time.js';

const sfxPools = new Map();
const sfxPoolCursor = new Map();
const activeSfxPlayers = new Set();
const sfxBuffers = new Map();
const sfxDecodePromises = new Map();
const activeSfxSources = new Set();

const audioState = {
  enabled: true,
  unlocked: false,
  ctx: null,
  masterGain: null,
  inFocus: isAppInFocus(),
  startSfxTimer: 0
};
const COUNTDOWN_SFX_FADE_SEC = 0.025;

function isAppInFocus() {
  const visible = typeof document.visibilityState !== 'string' || document.visibilityState === 'visible';
  const focused = typeof document.hasFocus !== 'function' || document.hasFocus();
  return visible && focused;
}

// Audio deliberately does NOT depend on window focus. A visible-but-unfocused
// window is still being watched (second monitor, or a lobby left open while the
// player reads something else) and the start countdown exists precisely so
// someone who has tabbed away knows to come back -- silencing it defeats its
// only purpose. Cues survive being away because they are scheduled on the
// AudioContext clock, which browsers do not throttle the way they clamp
// setTimeout in background tabs, so they still fire on time.
//
// The one thing worth doing on a focus change is the reverse of what this used
// to do: a browser may have suspended the context while we were away, so resume
// it on return rather than waiting for the next user gesture.
function updateAppFocusState() {
  const next = isAppInFocus();
  if (next === audioState.inFocus) return;
  audioState.inFocus = next;
  if (audioState.inFocus) {
    resumeAudioContext();
  }
}

function isTrustedGestureEvent(event) {
  if (!event || event.isTrusted === false) return false;
  const type = event.type;
  return (
    type === 'pointerdown'
    || type === 'mousedown'
    || type === 'touchstart'
    || type === 'keydown'
    || type === 'keyup'
    || type === 'click'
  );
}

function ensureAudioContext() {
  if (audioState.ctx) return audioState.ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    audioState.ctx = new Ctor();
    audioState.masterGain = audioState.ctx.createGain();
    audioState.masterGain.gain.value = audioState.enabled ? 1 : 0;
    audioState.masterGain.connect(audioState.ctx.destination);
    return audioState.ctx;
  } catch (_) {
    audioState.ctx = null;
    audioState.masterGain = null;
    return null;
  }
}

function resumeAudioContext() {
  const ctx = ensureAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => { });
  }
}

function decodeSfxBuffer(url) {
  if (!url || sfxBuffers.has(url) || sfxDecodePromises.has(url)) return;
  const ctx = ensureAudioContext();
  if (!ctx) return;

  const task = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error('sfx fetch failed');
      return response.arrayBuffer();
    })
    .then((arrayBuffer) => ctx.decodeAudioData(arrayBuffer))
    .then((buffer) => {
      if (buffer) {
        sfxBuffers.set(url, buffer);
      }
    })
    .catch(() => { })
    .finally(() => {
      sfxDecodePromises.delete(url);
    });

  sfxDecodePromises.set(url, task);
}

function getSfxPool(url, poolSize = SFX_POOL_DEFAULT) {
  const size = Math.max(1, poolSize | 0);
  const current = sfxPools.get(url);
  if (current && current.length === size) return current;
  const pool = Array.from({ length: size }, () => {
    const audio = new Audio(url);
    // Keep HTMLAudio fallback lazy; primary playback path is decoded WebAudio buffers.
    audio.preload = 'none';
    return audio;
  });
  sfxPools.set(url, pool);
  sfxPoolCursor.set(url, 0);
  return pool;
}

function pickSfxPlayer(url, poolSize = SFX_POOL_DEFAULT) {
  const pool = getSfxPool(url, poolSize);
  const cursor = sfxPoolCursor.get(url) || 0;
  const player = pool[cursor];
  sfxPoolCursor.set(url, (cursor + 1) % pool.length);
  return player;
}

function warmSfxAssets() {
  for (const url of Object.values(SFX)) {
    decodeSfxBuffer(url);
  }
}

function tryPlayBufferedSfx(url, volume = 0.35) {
  const ctx = audioState.ctx;
  const master = audioState.masterGain;
  const buffer = sfxBuffers.get(url);
  if (!ctx || !master || !buffer) return false;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => { });
    return false;
  }
  try {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gainNode = ctx.createGain();
    gainNode.gain.value = clamp(volume, 0, 1);
    source.connect(gainNode);
    gainNode.connect(master);
    source.onended = () => {
      activeSfxSources.delete(source);
      source.onended = null;
    };
    activeSfxSources.add(source);
    source.start(ctx.currentTime + WEB_AUDIO_START_ADVANCE_SEC);
    return true;
  } catch (_) {
    return false;
  }
}

function stopAllSfx() {
  for (const player of activeSfxPlayers) {
    try {
      player.pause();
      player.currentTime = 0;
    } catch (_) { }
  }
  activeSfxPlayers.clear();

  for (const source of activeSfxSources) {
    try {
      source.onended = null;
      source.stop();
    } catch (_) { }
  }
  activeSfxSources.clear();
  stopCountdownSfx();
}

function setSoundEnabled(next) {
  const enabled = Boolean(next);
  if (enabled === audioState.enabled) return;
  audioState.enabled = enabled;
  if (audioState.masterGain) {
    audioState.masterGain.gain.value = audioState.enabled ? 1 : 0;
  }
  if (!audioState.enabled) {
    stopAllSfx();
  }
  ui.controlsDirty = true;
}

function playSound(url, volume = 0.35, poolSize = SFX_POOL_DEFAULT) {
  updateAppFocusState();
  if (!audioState.unlocked || !audioState.enabled || !url) return;
  const level = clamp((Number(volume) || 0) * (settings.soundVolume / 100), 0, 1);
  if (level <= 0) return;
  if (!sfxBuffers.has(url)) {
    decodeSfxBuffer(url);
  }
  if (tryPlayBufferedSfx(url, level)) return;
  const player = pickSfxPlayer(url, poolSize);
  player.volume = level;
  player.muted = !audioState.enabled;
  try {
    player.currentTime = 0;
  } catch (_) { }
  const playPromise = player.play();
  if (playPromise && typeof playPromise.catch === 'function') {
    playPromise.catch(() => { });
  }
  activeSfxPlayers.add(player);
}

function markAudioInteraction(event) {
  if (event && event.isTrusted === false) return;
  updateAppFocusState();
  const userActivation = navigator.userActivation;
  const hasActiveGesture = (
    !userActivation
      ? isTrustedGestureEvent(event)
      : userActivation.isActive
  );
  const firstUnlock = !audioState.unlocked && hasActiveGesture;
  if (firstUnlock) {
    audioState.unlocked = true;
    warmSfxAssets();
  }
  if (hasActiveGesture) {
    resumeAudioContext();
  }
  if (firstUnlock) {
    flushScheduledStartSfx(Date.now());
  }
}

// Lobby start countdown: one authored clip (five blips and a Go) rather than a
// synthesized ladder, scheduled on the audio clock so it cannot drift from the
// number on the button.
//
// The clip is anchored by its Go, not by its start: the Go sits COUNTDOWN_GO_AT_SEC
// into the file and must land exactly when the match does, so the clip is placed
// at (countdown end - that offset). A player who hears about the countdown late
// starts partway into the buffer instead of from the top, which keeps every
// remaining blip on its real boundary.
const COUNTDOWN_GO_AT_SEC = 5;
// The clip peaks at ~0.62 on its Go; this puts that peak at the ~0.28 the old
// synthesized cue hit, so the lobby did not get louder. The blips are quieter
// than the Go by design -- that balance is the clip's, not ours.
const COUNTDOWN_SFX_GAIN = 0.45;
// Past this much of the clip already gone, there is nothing worth starting.
const COUNTDOWN_LATE_START_GRACE_SEC = 0.2;

let countdownEntry = null;
let countdownPlayToken = 0;

function scheduleCountdownClip(remainingSec, level) {
  const ctx = audioState.ctx;
  const master = audioState.masterGain;
  if (!ctx || !master) return false;
  if (ctx.state === 'suspended') return false;
  const buffer = sfxBuffers.get(SFX.countdown);
  if (!buffer) return false;

  // Where in the clip we should already be for its Go to land on time.
  const offsetSec = COUNTDOWN_GO_AT_SEC - remainingSec;
  if (offsetSec >= buffer.duration - COUNTDOWN_LATE_START_GRACE_SEC) return true;

  const now = ctx.currentTime + WEB_AUDIO_START_ADVANCE_SEC;
  const startAt = offsetSec < 0 ? now - offsetSec : now;
  const startOffset = Math.max(0, offsetSec);

  try {
    const gainNode = ctx.createGain();
    gainNode.gain.value = clamp(level, 0, 1);
    gainNode.connect(master);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gainNode);
    const entry = { source, gainNode };
    countdownEntry = entry;
    source.onended = () => {
      source.onended = null;
      if (countdownEntry === entry) countdownEntry = null;
      try { gainNode.disconnect(); } catch (_) { }
    };
    source.start(startAt, startOffset);
    return true;
  } catch (_) {
    return false;
  }
}

function playCountdownSfx(timing = {}) {
  updateAppFocusState();
  if (!audioState.unlocked || !audioState.enabled) return;
  const level = clamp(COUNTDOWN_SFX_GAIN * (settings.soundVolume / 100), 0, 1);
  if (level <= 0) return;
  stopCountdownSfx();
  const playToken = ++countdownPlayToken;
  const startedAtMs = Date.now();
  const durationSec = Math.max(0.5, (Number(timing.durationMs) || 5000) / 1000);
  const elapsedBaseSec = Math.max(0, (Number(timing.elapsedMs) || 0) / 1000);
  const attempt = () => {
    if (playToken !== countdownPlayToken) return true;
    // Recomputed per attempt: decoding or resuming the context takes time, and
    // the countdown keeps running while it does.
    const elapsedSec = elapsedBaseSec + (Date.now() - startedAtMs) / 1000;
    return scheduleCountdownClip(durationSec - elapsedSec, level);
  };
  if (attempt()) return;

  // Not playable yet: the context may be missing or suspended, and the clip may
  // still be undecoded. Fix whichever it is, then place what is left of it.
  const ctx = ensureAudioContext();
  if (!ctx) return;
  const decoding = sfxDecodePromises.get(SFX.countdown);
  if (!sfxBuffers.has(SFX.countdown) && !decoding) {
    decodeSfxBuffer(SFX.countdown);
  }
  Promise.resolve(sfxDecodePromises.get(SFX.countdown))
    .then(() => ctx.resume().catch(() => { }))
    .then(() => { attempt(); })
    .catch(() => { });
}

function stopCountdownSfx() {
  countdownPlayToken += 1;
  ui.suppressStartSfxUntil = 0;
  const entry = countdownEntry;
  countdownEntry = null;
  if (!entry) return;
  const ctx = audioState.ctx;
  const now = ctx ? ctx.currentTime : 0;
  try {
    if (ctx) {
      entry.gainNode.gain.cancelScheduledValues(now);
      entry.gainNode.gain.setValueAtTime(entry.gainNode.gain.value, now);
      entry.gainNode.gain.linearRampToValueAtTime(0, now + COUNTDOWN_SFX_FADE_SEC);
    }
  } catch (_) { }
  try {
    entry.source.onended = null;
    entry.source.stop(ctx ? now + COUNTDOWN_SFX_FADE_SEC : undefined);
  } catch (_) { }
  // onended is gone (and never fires at all for a clip stopped before its own
  // start time), so unhook the gain ourselves once the fade has run.
  setTimeout(() => {
    try { entry.gainNode.disconnect(); } catch (_) { }
  }, Math.ceil(COUNTDOWN_SFX_FADE_SEC * 1000) + 20);
}

function scheduleStartSfx(targetAtServerMs, sourceServerNowMs, localNowMs = Date.now()) {
  const target = Number(targetAtServerMs);
  const sourceNow = Number(sourceServerNowMs);
  let localTarget = localNowMs;
  const oneWayMs = estimatedOneWayMs();

  if (Number.isFinite(target) && target > 0 && Number.isFinite(sourceNow)) {
    const remainingMs = target - sourceNow;
    localTarget = localNowMs + Math.max(0, remainingMs - START_SFX_LEAD_MS - oneWayMs);
  } else if (Number.isFinite(target) && target > 0 && ui.clockSynced) {
    localTarget = target - ui.serverClockOffsetMs - START_SFX_LEAD_MS - oneWayMs;
  }

  ui.scheduledStartSfxAt = localTarget;
  ui.pendingStartSfx = true;
  armScheduledStartSfxTimer(localNowMs);
}

function clearScheduledStartSfx() {
  if (audioState.startSfxTimer) {
    clearTimeout(audioState.startSfxTimer);
    audioState.startSfxTimer = 0;
  }
  ui.pendingStartSfx = false;
  ui.scheduledStartSfxAt = 0;
}

function armScheduledStartSfxTimer(nowMs = Date.now()) {
  if (audioState.startSfxTimer) {
    clearTimeout(audioState.startSfxTimer);
    audioState.startSfxTimer = 0;
  }
  if (!ui.pendingStartSfx) return;

  const targetAt = ui.scheduledStartSfxAt > 0 ? ui.scheduledStartSfxAt : nowMs;
  const delay = Math.max(0, targetAt - nowMs - START_SFX_TIMER_LOOKAHEAD_MS);
  audioState.startSfxTimer = setTimeout(() => {
    audioState.startSfxTimer = 0;
    flushScheduledStartSfx(Date.now());
  }, delay);
}

function flushScheduledStartSfx(nowMs = Date.now()) {
  if (!ui.pendingStartSfx) return;
  const targetAt = ui.scheduledStartSfxAt > 0 ? ui.scheduledStartSfxAt : nowMs;
  if (nowMs + START_SFX_EARLY_WINDOW_MS < targetAt) {
    armScheduledStartSfxTimer(nowMs);
    return;
  }
  if (!audioState.unlocked) return;
  if (audioState.startSfxTimer) {
    clearTimeout(audioState.startSfxTimer);
    audioState.startSfxTimer = 0;
  }
  if (nowMs <= (Number(ui.suppressStartSfxUntil) || 0)) {
    ui.pendingStartSfx = false;
    ui.scheduledStartSfxAt = 0;
    return;
  }
  ui.pendingStartSfx = false;
  ui.scheduledStartSfxAt = 0;
  playSound(SFX.start, 0.42);
}

export {
  audioState,
  updateAppFocusState,
  ensureAudioContext,
  resumeAudioContext,
  stopAllSfx,
  setSoundEnabled,
  playSound,
  playCountdownSfx,
  stopCountdownSfx,
  markAudioInteraction,
  scheduleStartSfx,
  clearScheduledStartSfx,
  flushScheduledStartSfx
};
